import { forwardRef, type ImgHTMLAttributes } from 'react';

export type OptimizedImageProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  'loading' | 'decoding' | 'fetchPriority'
> & {
  /** Au-dessus de la ligne de flottaison : chargement immédiat + priorité réseau plus haute */
  priority?: boolean;
  /** Priorité fetch réseau (équivalent HTML `fetchpriority`). */
  fetchPriority?: 'high' | 'low' | 'auto';
};

/**
 * Image avec `loading` / `decoding` adaptés aux listes (grilles, parcours, podcasts…).
 * Le cache disque du navigateur repose surtout sur les en-têtes `Cache-Control` du serveur (`/uploads`).
 *
 * React 18.2 : `fetchPriority` camelCase sur `<img>` déclenche un avertissement — on passe `fetchpriority`.
 */
export const OptimizedImage = forwardRef<HTMLImageElement, OptimizedImageProps>(function OptimizedImage(
  { priority = false, fetchPriority, ...rest },
  ref,
) {
  const fetchpriority = fetchPriority ?? (priority ? 'high' : 'auto');
  return (
    <img
      ref={ref}
      {...rest}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      {...({ fetchpriority } as ImgHTMLAttributes<HTMLImageElement>)}
    />
  );
});
